import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthenticatedUserInfo } from '../services/Contexts/AuthenticatedUserInfoContext';
import { usePageTitleContext } from '../services/Contexts/PageTitleContext';
import { getDashboardPathForRole } from '../utils/dashboardNavigation';
import LoadingSpinner from './LoadingSpinner/LoadingSpinner';

/** Paths for marketing / auth pages that logged-in users must not access. */
export const GUEST_ONLY_PATHS = ['/', '/auth', '/signup'];

export const isGuestOnlyPath = (pathname) => GUEST_ONLY_PATHS.includes(pathname);

/**
 * Renders children only for guests. Authenticated users are sent to their dashboard.
 */
export const GuestOnlyRoute = ({ children }) => {
	const { userId, token, userRole, loading } = useAuthenticatedUserInfo();

	if (token && loading) {
		return <LoadingSpinner />;
	}

	const isAuthenticated = userId && token && userRole;

	if (isAuthenticated) {
		return <Navigate to={getDashboardPathForRole(userRole)} replace />;
	}

	return children;
};

export const PrivateRoute = ({
	children,
	pageTitle,
	pageDescription,
	allowedRoles,
}) => {
	const { userId, token, userRole, loading } = useAuthenticatedUserInfo();

	if (token && loading) {
		return <LoadingSpinner />;
	}

	const isAuthenticated = userId && token && userRole;

	const { titles, setPageTitle, descriptions, setPageDescription } =
		usePageTitleContext();
	const location = useLocation();

	React.useEffect(() => {
		if (pageTitle && titles[location.pathname] !== pageTitle) {
			setPageTitle(location.pathname, pageTitle);
		}
		if (
			pageDescription !== undefined &&
			descriptions[location.pathname] !== pageDescription
		) {
			setPageDescription(location.pathname, pageDescription);
		}
	}, [
		location.pathname,
		pageTitle,
		titles,
		setPageTitle,
		pageDescription,
		descriptions,
		setPageDescription,
	]);

	React.useEffect(() => {
		if (pageTitle) {
			document.title = `Ops Pilot CRM - ${pageTitle}`;
		}
	}, [pageTitle]);

	if (!isAuthenticated) {
		return <Navigate to='/auth' replace />;
	}

	if (allowedRoles && !allowedRoles.includes(userRole)) {
		return <Navigate to={getDashboardPathForRole(userRole)} replace />;
	}

	// ✅ Return children directly
	return children;
};

export const DashboardRedirect = () => {
	const { userRole } = useAuthenticatedUserInfo();

	if (userRole) {
		return <Navigate to={getDashboardPathForRole(userRole)} />;
	}
};

export const NotFoundOrNotAllowedRedirect = () => {
	const { userRole } = useAuthenticatedUserInfo();

	if (userRole) {
		return <Navigate to={getDashboardPathForRole(userRole)} replace />;
	}
	return <Navigate to='/auth' replace />;
};
